import React from "react";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";

/**
 * Shared Pagination Component
 * Can be used across the application for consistent pagination UI
 * 
 * @param {number} currentPage - Current active page (1-indexed)
 * @param {number} totalPages - Total number of pages
 * @param {number} totalCount - Total number of items
 * @param {number} pageSize - Items per page
 * @param {function} onPageChange - Callback when page changes
 * @param {boolean} isLoading - Loading state to disable buttons
 * @param {string} variant - 'default' | 'compact' | 'minimal' | 'dashboard' | 'location'
 * @param {string} position - 'top' | 'bottom' - affects styling and test IDs
 * @param {string} itemLabel - Label for items (default: 'results')
 */
export default function SharedPagination({
  currentPage,
  totalPages,
  totalCount,
  pageSize,
  onPageChange,
  isLoading = false,
  variant = "default",
  position = "bottom",
  itemLabel = null,
  className = "",
}) {
  const { t } = useTranslation();

  if (totalPages <= 1 && variant !== "minimal") {
    return null;
  }

  // Calculate display range
  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalCount);
  
  // Get item label (translated or custom)
  const getItemLabel = () => {
    if (itemLabel) return itemLabel;
    return t("pagination.results") || "results";
  };

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = variant === "compact" ? 3 : 5;
    
    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);
      
      // Calculate middle pages
      let startPage = Math.max(2, currentPage - 1);
      let endPage = Math.min(totalPages - 1, currentPage + 1);
      
      // Adjust if at the beginning or end
      if (currentPage <= 2) {
        endPage = Math.min(totalPages - 1, 3);
      } else if (currentPage >= totalPages - 1) {
        startPage = Math.max(2, totalPages - 2);
      }
      
      // Add ellipsis if needed before middle pages
      if (startPage > 2) {
        pages.push("...");
      }
      
      // Add middle pages
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }
      
      // Add ellipsis if needed after middle pages
      if (endPage < totalPages - 1) {
        pages.push("...");
      }
      
      // Always show last page
      if (totalPages > 1) {
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  const positionSuffix = position === "bottom" ? "-bottom" : "";

  // Minimal variant - just prev/next buttons with page indicator
  if (variant === "minimal") {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(Math.max(currentPage - 1, 1))}
          disabled={currentPage === 1 || isLoading}
          className="h-8 px-2"
          data-testid={`pagination-prev${positionSuffix}`}
        >
          <ChevronLeft className="w-4 h-4" />
        </Button>
        <span className="flex items-center px-3 text-sm text-slate-600">
          {currentPage} / {totalPages}
        </span>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(Math.min(currentPage + 1, totalPages))}
          disabled={currentPage === totalPages || isLoading}
          className="h-8 px-2"
          data-testid={`pagination-next${positionSuffix}`}
        >
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>
    );
  }

  // Compact variant - smaller, fewer page numbers
  if (variant === "compact") {
    return (
      <div className={`flex items-center justify-between gap-4 ${className}`}>
        <p className="text-xs text-slate-500">
          {startItem}-{endItem} {t("common.of") || "of"} {totalCount}
        </p>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onPageChange(Math.max(currentPage - 1, 1))}
            disabled={currentPage === 1 || isLoading}
            className="h-7 w-7 p-0"
            data-testid={`pagination-prev${positionSuffix}`}
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          {getPageNumbers().map((page, index) => (
            <React.Fragment key={index}>
              {page === "..." ? (
                <span className="px-1 text-slate-400 text-sm">...</span>
              ) : (
                <Button
                  variant={currentPage === page ? "default" : "ghost"}
                  size="sm"
                  onClick={() => onPageChange(page)}
                  disabled={isLoading}
                  className={`h-7 w-7 p-0 text-xs ${
                    currentPage === page
                      ? "bg-emerald-600 text-white"
                      : "text-slate-600 hover:bg-stone-100"
                  }`}
                  data-testid={`pagination-page-${page}${positionSuffix}`}
                >
                  {page}
                </Button>
              )}
            </React.Fragment>
          ))}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onPageChange(Math.min(currentPage + 1, totalPages))}
            disabled={currentPage === totalPages || isLoading}
            className="h-7 w-7 p-0"
            data-testid={`pagination-next${positionSuffix}`}
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>
    );
  }

  // Dashboard variant - matches existing OfficialDashboard styling
  if (variant === "dashboard") {
    return (
      <div className={`flex flex-col sm:flex-row items-start sm:items-center justify-between ${position === "top" ? "mb-4" : "mt-4"} px-4 py-3 bg-white rounded-lg border border-stone-200 shadow-sm gap-3 transition-opacity duration-200 ${isLoading ? 'opacity-60' : 'opacity-100'} ${className}`}>
        <div className="flex items-center gap-2 text-sm text-emerald-700 font-medium">
          <span>
            {t('pagination.showing')} <span className="font-bold">{startItem}</span> - <span className="font-bold">{endItem}</span> {t('pagination.of')} <span className="font-bold">{totalCount}</span> {t('pagination.applications') || getItemLabel()}
          </span>
        </div>
        
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(Math.max(currentPage - 1, 1))}
            disabled={currentPage === 1 || isLoading}
            className="text-emerald-700 border-emerald-200 hover:bg-emerald-50 disabled:opacity-50"
            data-testid={`pagination-prev${positionSuffix}`}
          >
            {t('pagination.previous')}
          </Button>
          
          <div className="flex items-center gap-1">
            {getPageNumbers().map((page, index) => (
              <React.Fragment key={index}>
                {page === "..." ? (
                  <span className="px-1 text-slate-400">...</span>
                ) : (
                  <Button
                    variant={currentPage === page ? "default" : "ghost"}
                    size="sm"
                    onClick={() => onPageChange(page)}
                    disabled={isLoading}
                    className={currentPage === page ? "bg-emerald-600 text-white" : "text-slate-600 hover:bg-stone-100"}
                    data-testid={`pagination-page-${page}${positionSuffix}`}
                  >
                    {page}
                  </Button>
                )}
              </React.Fragment>
            ))}
          </div>
          
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(Math.min(currentPage + 1, totalPages))}
            disabled={currentPage === totalPages || isLoading}
            className="text-emerald-700 border-emerald-200 hover:bg-emerald-50 disabled:opacity-50"
            data-testid={`pagination-next${positionSuffix}`}
          >
            {t('pagination.next')}
          </Button>
        </div>
      </div>
    );
  }

  // Location variant - matches LocationDetailedView styling
  if (variant === "location") {
    if (totalCount === 0) return null;
    
    return (
      <div className={`flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 p-4 ${position === "top" ? "border-b" : "border-t"} border-stone-200 bg-stone-50 ${className}`}>
        <span className="text-sm font-medium text-emerald-700">
          {t('pagination.showing')} {startItem} - {endItem} {t('pagination.of')} {totalCount} {t('locationReports.applications') || getItemLabel()}
        </span>
        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(Math.max(currentPage - 1, 1))}
            disabled={currentPage === 1 || isLoading}
            className="h-8 px-2"
            data-testid={`${position === "top" ? "prev" : "prev-bottom"}-page-btn`}
          >
            <ChevronLeft className="w-4 h-4" />
            <span className="hidden sm:inline ml-1">{t('pagination.previous')}</span>
          </Button>
          {getPageNumbers().map((page, index) => (
            <React.Fragment key={index}>
              {page === "..." ? (
                <span className="px-2 text-slate-400">...</span>
              ) : (
                <Button
                  variant={currentPage === page ? "default" : "outline"}
                  size="sm"
                  onClick={() => onPageChange(page)}
                  disabled={isLoading}
                  className={`h-8 w-8 p-0 ${currentPage === page ? 'bg-emerald-700 hover:bg-emerald-800' : ''}`}
                  data-testid={`page-${page}-btn${positionSuffix}`}
                >
                  {page}
                </Button>
              )}
            </React.Fragment>
          ))}
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(Math.min(currentPage + 1, totalPages))}
            disabled={currentPage === totalPages || isLoading}
            className="h-8 px-2"
            data-testid={`${position === "top" ? "next" : "next-bottom"}-page-btn`}
          >
            <span className="hidden sm:inline mr-1">{t('pagination.next')}</span>
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>
    );
  }

  // Default variant - full pagination
  return (
    <div
      className={`flex flex-col sm:flex-row items-center justify-between gap-4 ${
        position === "top" ? "pb-4 border-b border-stone-200" : ""
      } ${position === "bottom" ? "pt-4 border-t border-stone-200" : ""} ${className}`}
    >
      {/* Item count display */}
      <p className="text-sm text-slate-600">
        {t("pagination.showing") || "Showing"}{" "}
        <span className="font-medium">{startItem}</span> -{" "}
        <span className="font-medium">{endItem}</span>{" "}
        {t("common.of") || "of"}{" "}
        <span className="font-medium">{totalCount}</span>{" "}
        {getItemLabel()}
      </p>

      {/* Page navigation */}
      <div className="flex items-center gap-1">
        {/* Previous button */}
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(Math.max(currentPage - 1, 1))}
          disabled={currentPage === 1 || isLoading}
          className="h-8"
          data-testid={`pagination-prev${positionSuffix}`}
        >
          <ChevronLeft className="w-4 h-4 mr-1" />
          <span className="hidden sm:inline">
            {t("pagination.previous") || "Previous"}
          </span>
        </Button>

        {/* Page numbers */}
        <div className="hidden sm:flex items-center gap-1 mx-2">
          {getPageNumbers().map((page, index) => (
            <React.Fragment key={index}>
              {page === "..." ? (
                <span className="px-2 text-slate-400">...</span>
              ) : (
                <Button
                  variant={currentPage === page ? "default" : "ghost"}
                  size="sm"
                  onClick={() => onPageChange(page)}
                  disabled={isLoading}
                  className={`h-8 w-8 p-0 ${
                    currentPage === page
                      ? "bg-emerald-600 text-white hover:bg-emerald-700"
                      : "text-slate-600 hover:bg-stone-100"
                  }`}
                  data-testid={`pagination-page-${page}${positionSuffix}`}
                >
                  {page}
                </Button>
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Mobile page indicator */}
        <span className="sm:hidden text-sm text-slate-600 mx-2">
          {currentPage} / {totalPages}
        </span>

        {/* Next button */}
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(Math.min(currentPage + 1, totalPages))}
          disabled={currentPage === totalPages || isLoading}
          className="h-8"
          data-testid={`pagination-next${positionSuffix}`}
        >
          <span className="hidden sm:inline">
            {t("pagination.next") || "Next"}
          </span>
          <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </div>
    </div>
  );
}
